import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PenLine } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AnalyzeTranscript,
  ApplyIdeationSuggestion,
  createIdeationAIConnection,
  DeleteIdeationIdea,
  GetAnalysisStatus,
  GetIdeationIdea,
  INSUFFICIENT_FUNDS_EVENT,
  ListIdeationIdeas,
  RevertIdeationVersion,
  UpsertIdeationIdea,
} from '../../Client/ManagementServiceClient';
import StructuredAnalysisView from '../VideoAnalysis/AnalysisDetails/StructuredAnalysisView';
import { FloatingChatDock } from '../FloatingChat';
import { useAuth } from '../../hooks/useAuth';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import './styles.css';

// Roughly 30s of retries (1+2+4+8+15s) before telling the user to reload.
const MAX_AI_RECONNECT_ATTEMPTS = 5;

const computeTextHash = (text) => {
  const value = String(text || '');
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return `txt_${(hash >>> 0).toString(16)}`;
};

const createEmptyVersion = (seedText = '') => {
  const now = new Date().toISOString();
  return {
    id: `idea_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text: seedText,
    hash: computeTextHash(seedText),
    status: 'draft',
    analysisId: null,
    analysisStatus: null,
    analysisResults: null,
    analysisTextHash: null,
    version: 1,
    versionHistory: [],
    createdAt: now,
    updatedAt: now,
    finalizedAt: null,
    videoWorkspaceId: null,
  };
};

const normalizeIdea = (idea) => {
  if (!idea) return null;
  return {
    id: idea.idea_id || idea.id || `idea_${Date.now()}`,
    text: String(idea.text || ''),
    hash: idea.text_hash || computeTextHash(idea.text || ''),
    status: idea.status || 'draft',
    analysisId: idea.analysis_id || null,
    analysisStatus: idea.analysis_status || null,
    analysisResults: idea.analysis_results || null,
    analysisTextHash: idea.analysis_text_hash || null,
    version: Number(idea.version || 1),
    versionHistory: Array.isArray(idea.version_history) ? idea.version_history : [],
    createdAt: idea.created_at || new Date().toISOString(),
    updatedAt: idea.updated_at || new Date().toISOString(),
    finalizedAt: idea.finalized_at || null,
    videoWorkspaceId: idea.video_workspace_id || null,
  };
};

// A label for the draft list, derived from the text on the fly. An idea stores
// no name of its own -- the project's name lives on the workflow.
const getIdeaLabel = (text, fallbackLabel) => {
  const normalized = String(text || '').trim();
  if (!normalized) return fallbackLabel;
  const firstLine = normalized.split('\n').map((line) => line.trim()).find(Boolean) || normalized;
  return firstLine.length > 48 ? `${firstLine.slice(0, 48)}…` : firstLine;
};

const getIdeaPreview = (text) => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return 'Empty draft';
  return normalized.length > 95 ? `${normalized.slice(0, 95)}…` : normalized;
};

const extractJsonAndNarrative = (value) => {
  if (value == null) {
    return { jsonValues: [], narrative: '' };
  }

  if (typeof value === 'object') {
    return { jsonValues: [value], narrative: '' };
  }

  const text = String(value || '').trim();
  if (!text) {
    return { jsonValues: [], narrative: '' };
  }

  const jsonValues = [];
  let narrative = text;

  const fencedMatches = [...text.matchAll(/```json\s*([\s\S]*?)```/gi)];
  if (fencedMatches.length > 0) {
    fencedMatches.forEach((match) => {
      const candidate = String(match[1] || '').trim();
      try {
        if (candidate.startsWith('{') || candidate.startsWith('[')) {
          jsonValues.push(JSON.parse(candidate));
        }
      } catch {
        // Ignore malformed fenced payloads.
      }
    });
    narrative = text.replace(/```json\s*[\s\S]*?```/gi, '').trim();
    return { jsonValues, narrative };
  }

  // Fallback: detect a single inline JSON object/array within free text.
  const startObj = text.indexOf('{');
  const endObj = text.lastIndexOf('}');
  const startArr = text.indexOf('[');
  const endArr = text.lastIndexOf(']');

  const tryCandidates = [];
  if (startObj >= 0 && endObj > startObj) {
    tryCandidates.push([startObj, endObj]);
  }
  if (startArr >= 0 && endArr > startArr) {
    tryCandidates.push([startArr, endArr]);
  }

  for (let i = 0; i < tryCandidates.length; i += 1) {
    const [start, end] = tryCandidates[i];
    const candidate = text.slice(start, end + 1).trim();
    try {
      const parsed = JSON.parse(candidate);
      jsonValues.push(parsed);
      narrative = `${text.slice(0, start)} ${text.slice(end + 1)}`.trim();
      break;
    } catch {
      // Try next candidate
    }
  }

  return { jsonValues, narrative };
};

const renderNarrativeSection = (narrative) => {
  const paragraphs = String(narrative || '')
    .split(/\n\s*\n/g)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  return paragraphs.map((paragraph, idx) => {
    const lines = paragraph.split('\n').map((line) => line.trim()).filter(Boolean);
    const isList = lines.length > 1 && lines.every((line) => line.startsWith('- '));

    if (isList) {
      return (
        <ul key={`list-${idx}`}>
          {lines.map((line) => <li key={`${idx}-${line.slice(0, 20)}`}>{line.slice(2)}</li>)}
        </ul>
      );
    }

    if (paragraph.startsWith('**') && paragraph.includes('**:')) {
      const markerEnd = paragraph.indexOf('**:');
      const label = paragraph.slice(2, markerEnd);
      const rest = paragraph.slice(markerEnd + 3).trim();
      return <p key={`p-${idx}`}><strong>{label}:</strong> {rest}</p>;
    }

    return <p key={`p-${idx}`}>{paragraph.replace(/\*\*/g, '')}</p>;
  });
};

const hasStructuredSections = (analysisResults) => {
  if (!analysisResults || typeof analysisResults !== 'object') return false;

  const analysisData = analysisResults?.analysis || analysisResults;
  if (!analysisData || typeof analysisData !== 'object') return false;

  const hasVisualData =
    (Array.isArray(analysisData.detected_objects) && analysisData.detected_objects.length > 0)
    || (Array.isArray(analysisData.detected_actions) && analysisData.detected_actions.length > 0)
    || typeof analysisData.monotony_score === 'number'
    || typeof analysisData.engagement_score === 'number'
    || (Array.isArray(analysisData.suggestions) && analysisData.suggestions.length > 0);

  return Boolean(
    // The merged verdict comes first: it is the only section an idea-stage
    // analysis renders, so a pass where every peer failed but retention still
    // scored has something to show and must not be treated as empty.
    analysisData.retention_structure
    || analysisData.metadata
    || analysisData.visual_suggestions
    || analysisData.persona_reactors
    || analysisData.clarity_engagement
    || analysisData.claims_risk
    || analysisData.cta_alignment
    || analysisData.trigger_sensitivity
    || analysisData.research
    || hasVisualData
  );
};

const extractAnalysisId = (responsePayload) => {
  if (!responsePayload || typeof responsePayload !== 'object') {
    return '';
  }

  const entities = responsePayload.Entities ?? responsePayload.entities;

  if (Array.isArray(entities) && entities.length > 0) {
    return String(entities[0] || '').trim();
  }

  if (entities && typeof entities === 'object') {
    const firstEntity = Object.values(entities)[0];
    return String(firstEntity || '').trim();
  }

  return String(
    responsePayload.analysis_id
    || responsePayload.AnalysisId
    || responsePayload.id
    || ''
  ).trim();
};

const computeLineDiffSummary = (currentText, suggestedText) => {
  const currentLines = String(currentText || '').split('\n');
  const suggestedLines = String(suggestedText || '').split('\n');
  const maxLines = Math.max(currentLines.length, suggestedLines.length);

  let changed = 0;
  let added = 0;
  let removed = 0;

  for (let i = 0; i < maxLines; i += 1) {
    const before = currentLines[i];
    const after = suggestedLines[i];

    if (before === undefined && after !== undefined) {
      added += 1;
      continue;
    }

    if (before !== undefined && after === undefined) {
      removed += 1;
      continue;
    }

    if (before !== after) {
      changed += 1;
    }
  }

  return { changed, added, removed };
};

function Ideation({
  forcedIdeaId = '',
  hideWorkflowNav = true,
  hideHeader = false,
  lockToIdea = false,
  hideSidebar = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [workingText, setWorkingText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingIdeas, setIsLoadingIdeas] = useState(true);
  const [hasMoreIdeas, setHasMoreIdeas] = useState(false);
  const [lastIdeaCursor, setLastIdeaCursor] = useState('');
  const [isIdeaDirty, setIsIdeaDirty] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiSuggestedText, setAiSuggestedText] = useState('');
  const [aiConnected, setAiConnected] = useState(false);
  // The AI socket is opened on first use, not on arrival: most visits to the
  // Idea step are writing, and an idle chat session still costs a connection
  // (and an agent session) on the server for the whole time the step is open.
  const [aiRequested, setAiRequested] = useState(false);
  const [aiWorking, setAiWorking] = useState(false);
  // The chat is docked, not inline, so it starts collapsed — closing it only
  // hides the panel and never drops the session or the transcript.
  const [isChatOpen, setIsChatOpen] = useState(false);
  // The chat offers the analysis rather than a button in the page doing it:
  // {analysisId, status: 'open'|'answered'}. Scoped to the selected idea and
  // cleared with the transcript, so dismissing it is never a dead end — come
  // back to the idea and the offer is made again.
  const [analysisOffer, setAnalysisOffer] = useState(null);
  const [aiReconnecting, setAiReconnecting] = useState(false);
  const [revertingVersion, setRevertingVersion] = useState(0);
  const pollingRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const lastAutosaveSignatureRef = useRef('');
  // Latest editor buffers, so the async autosave can tell whether the user kept
  // typing while a save was in flight (and never clobber it).
  const workingTextRef = useRef('');
  const analysisHydrationInFlightRef = useRef(new Set());
  const ideationWsRef = useRef(null);
  // Messages typed before the socket finished opening; flushed on session.started.
  const pendingAiPayloadsRef = useRef([]);
  // Read by the socket's close handler, which closes over the effect and cannot
  // see current state — and must not read it through a setState updater, since
  // those run twice under StrictMode and would duplicate the message.
  const aiWorkingRef = useRef(false);

  const requestedIdeaIdFromLocation = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('ideaId') || '').trim();
  }, [location.search]);
  const requestedIdeaId = String(forcedIdeaId || requestedIdeaIdFromLocation || '').trim();

  const selectedVersion = useMemo(
    () => versions.find((item) => item.id === selectedVersionId) || null,
    [versions, selectedVersionId]
  );

  const cacheMap = useMemo(() => {
    const map = {};
    versions.forEach((item) => {
      if (item.hash && item.analysisResults) {
        map[item.hash] = item.analysisResults;
      }
    });
    return map;
  }, [versions]);

  const suggestionDiff = useMemo(
    () => computeLineDiffSummary(workingText, aiSuggestedText),
    [workingText, aiSuggestedText]
  );

  // A suggestion identical to the draft is a no-op — say so instead of offering
  // an "Apply" that silently does nothing and reads as the AI ignoring you.
  const isSuggestionUnchanged = Boolean(aiSuggestedText)
    && String(aiSuggestedText).trim() === String(workingText).trim();

  useEffect(() => {
    aiWorkingRef.current = aiWorking;
  }, [aiWorking]);

  // The transcript, the pending suggestion and the analysis offer all describe
  // ONE draft. Carrying them across a switch would leave a rewrite of an idea
  // that is no longer on screen sitting next to a different script.
  useEffect(() => {
    setAiMessages([]);
    setAiInput('');
    setAiSuggestedText('');
    setAnalysisOffer(null);
  }, [selectedVersionId]);

  // Replaces the "Improve script with analysis" button: the collaborator raises
  // the analysis itself, in the place the rewrite is going to arrive. Opening
  // the dock IS the prompt — an offer nobody sees is the button all over again.
  const analysisOfferId = analysisOffer?.analysisId || '';
  useEffect(() => {
    const analysisId = String(selectedVersion?.analysisId || '').trim();
    if (!analysisId || selectedVersion?.analysisStatus !== 'completed') return;
    if (analysisId === analysisOfferId) return;

    setAnalysisOffer({ analysisId, status: 'open' });
    setIsChatOpen(true);
  }, [selectedVersion?.analysisId, selectedVersion?.analysisStatus, analysisOfferId]);

  const mergeIdeas = useCallback((prevIdeas, incomingIdeas) => {
    const mergedById = new Map();
    [...prevIdeas, ...incomingIdeas].forEach((idea) => {
      if (!idea?.id) return;
      mergedById.set(idea.id, idea);
    });

    return Array.from(mergedById.values()).sort((a, b) => {
      const aTs = Date.parse(a.updatedAt || '') || 0;
      const bTs = Date.parse(b.updatedAt || '') || 0;
      return bTs - aTs;
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadIdeas = async () => {
      if (!user) {
        setVersions([]);
        setSelectedVersionId(null);
        setWorkingText('');
        setIsIdeaDirty(false);
        setIsLoadingIdeas(false);
        return;
      }

      try {
        setIsLoadingIdeas(true);
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        const result = await ListIdeationIdeas(idToken, '', 25);
        if (result.status !== 200) {
          throw new Error('Failed to load ideas');
        }

        const ideas = Array.isArray(result.myJson?.ideas)
          ? result.myJson.ideas.map(normalizeIdea).filter(Boolean)
          : [];
        const hasMore = Boolean(result.myJson?.has_more);
        const nextCursor = String(result.myJson?.next_cursor_idea_id || '');

        if (isCancelled) return;

        if (!ideas.length) {
          const created = await UpsertIdeationIdea(idToken, { text: '' });
          if (created.status === 200 && created.myJson) {
            const firstIdea = normalizeIdea(created.myJson);
            setVersions([firstIdea]);
            setSelectedVersionId(firstIdea.id);
            setWorkingText(firstIdea.text || '');
            setIsIdeaDirty(false);
            setHasMoreIdeas(false);
            setLastIdeaCursor(firstIdea.id);
          } else {
            const localFallback = createEmptyVersion('');
            setVersions([localFallback]);
            setSelectedVersionId(localFallback.id);
            setWorkingText('');
            setIsIdeaDirty(false);
            setHasMoreIdeas(false);
            setLastIdeaCursor(localFallback.id);
          }
        } else {
          const requestedIdea = requestedIdeaId
            ? ideas.find((item) => item.id === requestedIdeaId)
            : null;
          const initialSelection = requestedIdea || ideas[0];

          setVersions(ideas);
          setSelectedVersionId(initialSelection.id);
          setWorkingText(String(initialSelection.text || ''));
          setIsIdeaDirty(false);
          setHasMoreIdeas(hasMore);
          setLastIdeaCursor(nextCursor || ideas[ideas.length - 1]?.id || '');
        }
      } catch (ex) {
        setError(ex.message || 'Failed to load ideas.');
      } finally {
        if (!isCancelled) {
          setIsLoadingIdeas(false);
        }
      }
    };

    loadIdeas();

    return () => {
      isCancelled = true;
    };
  }, [user, requestedIdeaId]);

  const loadMoreIdeas = useCallback(async () => {
    if (lockToIdea) {
      return;
    }

    if (!user || !hasMoreIdeas || !lastIdeaCursor) {
      return;
    }

    const auth = getAuth();
    const idToken = await auth.currentUser.getIdToken();
    const result = await ListIdeationIdeas(idToken, lastIdeaCursor, 25);
    if (result.status !== 200) {
      throw new Error('Failed to load more ideas');
    }

    const moreIdeas = Array.isArray(result.myJson?.ideas)
      ? result.myJson.ideas.map(normalizeIdea).filter(Boolean)
      : [];

    setVersions((prev) => mergeIdeas(prev, moreIdeas));
    setHasMoreIdeas(Boolean(result.myJson?.has_more));
    setLastIdeaCursor(String(result.myJson?.next_cursor_idea_id || lastIdeaCursor));
  }, [user, hasMoreIdeas, lastIdeaCursor, mergeIdeas, lockToIdea]);

  const { lastElementRef, isLoading: isLoadingMoreIdeas } = useInfiniteScroll(loadMoreIdeas, hasMoreIdeas && !lockToIdea);

  const visibleVersions = useMemo(() => {
    if (!lockToIdea || !requestedIdeaId) {
      return versions;
    }

    return versions.filter((item) => item.id === requestedIdeaId);
  }, [versions, lockToIdea, requestedIdeaId]);
  const showIdeaSidebar = !lockToIdea && !hideSidebar;

  useEffect(() => {
    workingTextRef.current = workingText;
  }, [workingText]);

  useEffect(() => {
    if (!selectedVersionId) return;

    const timer = setTimeout(() => {
      setVersions((prev) => prev.map((item) => {
        if (item.id !== selectedVersionId) return item;
        const nextText = workingText;
        const nextHash = computeTextHash(nextText);
        if (nextText === String(item.text || '')) return item;

        return {
          ...item,
          text: nextText,
          hash: nextHash,
          status: 'draft',
          analysisId: item.hash !== nextHash ? null : item.analysisId,
          analysisStatus: item.hash !== nextHash ? null : item.analysisStatus,
          analysisResults: item.hash !== nextHash ? null : item.analysisResults,
          updatedAt: new Date().toISOString(),
        };
      }));
      setIsIdeaDirty(true);
    }, 450);

    return () => clearTimeout(timer);
  }, [workingText, selectedVersionId]);

  useEffect(() => {
    if (!user || !selectedVersionId || isLoadingIdeas || !isIdeaDirty) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        const signature = `${selectedVersionId}:${workingText}`;
        const latestSignature = () => `${selectedVersionId}:${workingTextRef.current}`;
        if (lastAutosaveSignatureRef.current === signature) {
          // Already saved this exact content; clear the pending flag if the
          // buffer still matches (don't strand the "Saving…" indicator).
          if (latestSignature() === signature) setIsIdeaDirty(false);
          return;
        }

        const result = await UpsertIdeationIdea(idToken, {
          idea_id: selectedVersionId,
          text: workingText,
        });

        if (result.status !== 200 || !result.myJson) {
          throw new Error(result.myJson?.detail?.Message || result.myJson?.detail || 'Autosave failed');
        }

        const saved = normalizeIdea(result.myJson);
        // Refresh sidebar/version metadata from the server, but keep the freshest
        // local text — never echo the server response back into the editor
        // buffer. Writing it back is what clobbered live typing (reverted text,
        // jumped the cursor) when the user kept typing during the save.
        setVersions((prev) => prev.map((item) => {
          if (item.id !== selectedVersionId) return item;
          return { ...saved, text: item.text, hash: item.hash };
        }));
        lastAutosaveSignatureRef.current = signature;

        // Only mark "saved" if nothing changed since this save started; if the
        // user typed more while it was in flight, stay dirty so the newer text
        // autosaves on the next cycle.
        if (latestSignature() === signature) {
          setIsIdeaDirty(false);
        }
      } catch {
        // Autosave failures are non-blocking.
      }
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [user, selectedVersionId, workingText, isLoadingIdeas, isIdeaDirty]);

  useEffect(() => () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    if (ideationWsRef.current) {
      ideationWsRef.current.close();
      ideationWsRef.current = null;
    }
  }, []);

  useEffect(() => {
    const ideaId = String(selectedVersionId || '').trim();
    if (!user || !ideaId || !aiRequested) {
      return undefined;
    }

    let isDisposed = false;
    let ws = null;
    let reconnectTimer = null;
    let attempt = 0;

    // A dropped socket used to be terminal: the panel just went quiet and every
    // later message queued forever behind `aiConnected`. Retry with backoff, and
    // let the queue in `sendAiPayload` flush on the next session.started.
    const scheduleReconnect = () => {
      if (isDisposed) return;
      if (attempt >= MAX_AI_RECONNECT_ATTEMPTS) {
        setAiReconnecting(false);
        setAiMessages((prev) => [...prev, {
          role: 'system',
          text: 'Lost the connection to the AI and could not get it back. Reload the page to keep going.',
        }]);
        return;
      }

      const delay = Math.min(15000, 1000 * (2 ** attempt));
      attempt += 1;
      setAiReconnecting(true);
      reconnectTimer = setTimeout(connect, delay);
    };

    function connect() {
      if (isDisposed) return;

      ws = createIdeationAIConnection(ideaId);
      ideationWsRef.current = ws;
      bindHandlers(ws);
    }

    function bindHandlers(socket) {
    socket.onopen = async () => {
      try {
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();
        if (isDisposed) return;
        socket.send(JSON.stringify({ type: 'auth', token }));
      } catch {
        setAiConnected(false);
      }
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        const type = String(payload.type || '');

        if (type === 'session.started') {
          setAiConnected(true);
          setAiReconnecting(false);
          // A session that actually started is the only proof the endpoint is
          // healthy, so the backoff resets here rather than on socket open.
          attempt = 0;
          // Whatever the user sent while this was still opening (or while it
          // was down and reconnecting).
          const queued = pendingAiPayloadsRef.current;
          pendingAiPayloadsRef.current = [];
          queued.forEach((payload) => {
            try {
              socket.send(JSON.stringify(payload));
            } catch {
              setAiWorking(false);
            }
          });
          return;
        }

        if (type === 'analysis.context.loaded') {
          const headline = String(payload.headline || 'Analysis loaded into this conversation.');
          const notes = [];
          if (payload.truncated) notes.push('some lower-priority notes were trimmed to fit');
          if (Array.isArray(payload.agents_failed) && payload.agents_failed.length > 0) {
            notes.push(`${payload.agents_failed.length} analysis section(s) were unavailable`);
          }
          const text = notes.length ? `${headline} (${notes.join('; ')})` : headline;
          setAiMessages((prev) => [...prev, { role: 'system', text }]);
          return;
        }

        if (type === 'agent.plan') {
          setAiWorking(true);
          setAiMessages((prev) => [...prev, { role: 'assistant', text: String(payload.message || 'Planning...') }]);
          return;
        }

        if (type === 'agent.patch.proposed') {
          setAiWorking(false);
          const assistantMessage = String(payload.assistant_message || 'I have a refinement suggestion.');
          const summary = String(payload.summary_of_changes || '').trim();
          setAiSuggestedText(String(payload.suggested_text || ''));
          setAiMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: summary ? `${assistantMessage}\n\nChanges: ${summary}` : assistantMessage,
              usage: payload.usage || null,
            },
          ]);
          return;
        }

        if (type === 'idea.updated') {
          const nextText = String(payload.text || '');
          const nextHash = payload.text_hash || computeTextHash(nextText);
          setWorkingText(nextText);
          // The server already wrote this exact text -- prime the autosave
          // dedupe guard so the trailing 1500ms autosave (which still fires)
          // is a no-op instead of a byte-diff that would hit Upsert's
          // wipe-on-text-change block and destroy the binding apply_ai_text
          // just preserved.
          lastAutosaveSignatureRef.current = `${ideaId}:${nextText}`;
          setVersions((prev) => prev.map((item) => {
            if (item.id !== ideaId) return item;
            return {
              ...item,
              text: nextText,
              hash: nextHash,
              updatedAt: payload.updated_at || new Date().toISOString(),
              status: 'draft',
              // Preserved server-side by apply_ai_text -- no longer nulled here.
              analysisId: payload.analysis_id !== undefined ? payload.analysis_id : item.analysisId,
              analysisStatus: payload.analysis_status !== undefined ? payload.analysis_status : item.analysisStatus,
              analysisTextHash: payload.analysis_text_hash !== undefined ? payload.analysis_text_hash : item.analysisTextHash,
              version: payload.version !== undefined ? Number(payload.version) : item.version,
              versionHistory: Array.isArray(payload.version_history) ? payload.version_history : item.versionHistory,
            };
          }));
          setIsIdeaDirty(false);
          return;
        }

        if (type === 'agent.error' || type === 'error') {
          setAiWorking(false);
          // A websocket turn cannot return a 402, so an out-of-funds refusal
          // arrives as a coded error. Raise the same top-up dialog the HTTP
          // paths do rather than leaving it as chat text the user must decode.
          if (payload.code === 'insufficient_funds') {
            window.dispatchEvent(new CustomEvent(INSUFFICIENT_FUNDS_EVENT, { detail: payload }));
          }
          setAiMessages((prev) => [...prev, { role: 'assistant', text: String(payload.message || 'AI request failed.') }]);
          return;
        }

        if (type === 'agent.completed') {
          setAiWorking(false);
        }
      } catch {
        // Ignore malformed websocket payloads.
      }
    };

    socket.onclose = () => {
      if (isDisposed) return;
      setAiConnected(false);
      // A turn in flight when the socket dropped is gone — the server has no
      // way to deliver it to the new session. Say so rather than leaving the
      // panel stuck on "Working…" forever.
      if (aiWorkingRef.current) {
        setAiMessages((prev) => [...prev, {
          role: 'system',
          text: 'Connection dropped mid-request. Reconnecting — you will need to send that again.',
        }]);
      }
      setAiWorking(false);
      scheduleReconnect();
    };

    socket.onerror = () => {
      setAiConnected(false);
    };
    }

    connect();

    return () => {
      isDisposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ideationWsRef.current === ws) {
        ideationWsRef.current = null;
      }
      pendingAiPayloadsRef.current = [];
      if (ws) ws.close();
    };
  }, [user, selectedVersionId, aiRequested]);

  const handleCreateIteration = async () => {
    try {
      setError('');
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const created = await UpsertIdeationIdea(idToken, { text: '' });
      if (created.status !== 200 || !created.myJson) {
        throw new Error(created.myJson?.detail || 'Failed to create new idea');
      }

      const nextVersion = normalizeIdea(created.myJson) || createEmptyVersion('');
      setVersions((prev) => [nextVersion, ...prev]);
      setSelectedVersionId(nextVersion.id);
      setWorkingText(nextVersion.text || '');
      setIsIdeaDirty(false);
      setLastIdeaCursor((prev) => prev || nextVersion.id);
    } catch (ex) {
      setError(ex.message || 'Failed to start a new idea.');
    }
  };

  const handleSelectVersion = (versionId) => {
    const target = versions.find((item) => item.id === versionId);
    if (!target) return;
    setSelectedVersionId(versionId);
    setWorkingText(String(target.text || ''));
    setIsIdeaDirty(false);
    setError('');
  };

  const handleOpenIdeaWorkspace = (idea) => {
    const workspaceId = String(idea?.videoWorkspaceId || '').trim();
    if (!workspaceId) {
      setError('No linked workspace found for this idea yet.');
      return;
    }

    navigate(`/Editing?workspaceId=${encodeURIComponent(workspaceId)}`);
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!ideaId) return;
    if (!window.confirm('Delete this idea? This cannot be undone.')) return;

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const result = await DeleteIdeationIdea(idToken, ideaId);
      if (result.status !== 200) {
        throw new Error(result.myJson?.detail || 'Failed to delete idea');
      }

      const remaining = versions.filter((item) => item.id !== ideaId);
      setVersions(remaining);

      if (selectedVersionId === ideaId) {
        if (remaining.length > 0) {
          setSelectedVersionId(remaining[0].id);
          setWorkingText(String(remaining[0].text || ''));
        } else {
          await handleCreateIteration();
        }
      }

      if (remaining.length > 0) {
        setLastIdeaCursor(remaining[remaining.length - 1].id);
      }
    } catch (ex) {
      setError(ex.message || 'Failed to delete idea.');
    }
  };

  // Send now if the socket is up, otherwise open it and queue the payload for
  // session.started. Asking the user to "try again in a moment" was only ever
  // possible because the connection was opened eagerly on mount.
  const sendAiPayload = (payload) => {
    const ws = ideationWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && aiConnected) {
      ws.send(JSON.stringify(payload));
      return;
    }

    pendingAiPayloadsRef.current = [...pendingAiPayloadsRef.current, payload];
    setAiRequested(true);
  };

  const handleSendAiMessage = () => {
    const message = String(aiInput || '').trim();
    if (!message) return;

    setAiMessages((prev) => [...prev, { role: 'user', text: message }]);
    setAiInput('');
    setAiWorking(true);

    sendAiPayload({
      type: 'chat',
      message,
      current_text: workingText,
      use_research: true,
      apply_changes: false,
    });
  };

  // Narrowing the improvement is a normal chat message now ("just the hook"),
  // so the standalone focus box is gone — one less empty field to skip past.
  const handleImproveWithAnalysis = () => {
    setAiMessages((prev) => [...prev, { role: 'user', text: 'Improve the script with the analysis' }]);
    setAiWorking(true);

    sendAiPayload({
      type: 'improve_with_analysis',
      current_text: workingText,
      focus: '',
      use_research: false,
      apply_changes: false,
    });
  };

  const handleAcceptAnalysisOffer = () => {
    setAnalysisOffer((prev) => (prev ? { ...prev, status: 'answered' } : null));
    setIsChatOpen(true);
    handleImproveWithAnalysis();
  };

  const handleDismissAnalysisOffer = () => {
    setAnalysisOffer((prev) => (prev ? { ...prev, status: 'answered' } : null));
  };

  // Rejecting a draft has to be as easy as taking it — without this the only
  // way out of a rewrite you did not want was to apply it and undo, or reload.
  const handleRejectAiSuggestion = () => {
    setAiSuggestedText('');
    setAiMessages((prev) => [
      ...prev,
      { role: 'system', text: 'Suggestion discarded. Your draft is unchanged — tell me what to try instead.' },
    ]);
  };

  const handleApplyAiSuggestion = async () => {
    const nextText = String(aiSuggestedText || '').trim();
    if (!nextText || !selectedVersionId) return;

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const lastAssistantMessage = [...aiMessages].reverse().find((msg) => msg.role === 'assistant');
      const result = await ApplyIdeationSuggestion(idToken, selectedVersionId, {
        text: nextText,
        summary_of_changes: String(lastAssistantMessage?.text || ''),
      });

      if (result.status !== 200 || !result.myJson) {
        throw new Error(result.myJson?.detail?.Message || result.myJson?.detail || 'Failed to apply AI suggestion');
      }

      const saved = normalizeIdea(result.myJson);
      setVersions((prev) => prev.map((item) => (item.id === selectedVersionId ? saved : item)));
      setWorkingText(String(saved?.text || nextText));
      setIsIdeaDirty(false);
      // The server already wrote this exact text -- prime the autosave dedupe
      // guard so the trailing 1500ms autosave is a no-op instead of a byte-diff
      // that would wipe the analysis binding ApplySuggestion just preserved.
      lastAutosaveSignatureRef.current = `${selectedVersionId}:${saved?.text || nextText}`;
      setAiSuggestedText('');
      setAiMessages((prev) => [
        ...prev,
        { role: 'assistant', text: `Applied suggested draft — now version ${saved?.version || ''}.` },
      ]);
    } catch (ex) {
      setError(ex.message || 'Failed to apply AI suggestion.');
    }
  };

  const handleRevertToVersion = async (version) => {
    if (!selectedVersionId || !version) return;
    if (!window.confirm(`Revert to version ${version}? This creates a new version rather than deleting history.`)) return;

    setRevertingVersion(version);
    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const result = await RevertIdeationVersion(idToken, selectedVersionId, version);
      if (result.status !== 200 || !result.myJson) {
        throw new Error(result.myJson?.detail?.Message || result.myJson?.detail || 'Failed to revert idea');
      }

      const saved = normalizeIdea(result.myJson);
      setVersions((prev) => prev.map((item) => (item.id === selectedVersionId ? saved : item)));
      setWorkingText(String(saved?.text || ''));
      setIsIdeaDirty(false);
      lastAutosaveSignatureRef.current = `${selectedVersionId}:${saved?.text || ''}`;
    } catch (ex) {
      setError(ex.message || 'Failed to revert idea.');
    } finally {
      setRevertingVersion(0);
    }
  };

  useEffect(() => {
    const hydrateAnalysisFromId = async () => {
      if (!user || !selectedVersion || !selectedVersion.analysisId) return;
      if (selectedVersion.analysisResults && selectedVersion.analysisStatus === 'completed') return;

      const hydrationKey = `${selectedVersion.id}:${selectedVersion.analysisId}:${selectedVersion.hash}`;
      if (analysisHydrationInFlightRef.current.has(hydrationKey)) return;

      analysisHydrationInFlightRef.current.add(hydrationKey);
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        const result = await GetAnalysisStatus(idToken, selectedVersion.analysisId);
        if (result.status !== 200) return;

        const state = result.myJson?.status || null;
        const analysisResults = result.myJson?.analysis_results || null;

        setVersions((prev) => prev.map((item) => {
          if (item.id !== selectedVersion.id) return item;
          return {
            ...item,
            analysisStatus: state || item.analysisStatus,
            analysisResults: analysisResults || item.analysisResults,
            status: state === 'completed' ? 'analyzed' : item.status,
            updatedAt: new Date().toISOString(),
          };
        }));

        if (state && state !== 'completed' && state !== 'failed') {
          setIsAnalyzing(true);
          await pollAnalysisUntilDone(idToken, selectedVersion.analysisId, selectedVersion.id, selectedVersion.hash);
        }
      } catch {
        // Hydration is best-effort.
      } finally {
        analysisHydrationInFlightRef.current.delete(hydrationKey);
      }
    };

    hydrateAnalysisFromId();
  }, [user, selectedVersion]);

  const pollAnalysisUntilDone = async (idToken, analysisId, versionId, textHash) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(async () => {
      try {
        const result = await GetAnalysisStatus(idToken, analysisId);
        if (result.status !== 200) {
          return;
        }

        const state = result.myJson?.status;
        const analysisResults = result.myJson?.analysis_results;

        setVersions((prev) => prev.map((item) => {
          if (item.id !== versionId) return item;
          return {
            ...item,
            analysisStatus: state || item.analysisStatus,
            analysisResults: analysisResults || item.analysisResults,
            status: state === 'completed' ? 'analyzed' : item.status,
            updatedAt: new Date().toISOString(),
          };
        }));

        if (state === 'completed' || state === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsAnalyzing(false);

          if (state === 'failed') {
            setError(result.myJson?.error || 'Analysis failed.');
            return;
          }

          if (analysisResults) {
            setVersions((prev) => prev.map((item) => {
              if (item.id !== versionId) return item;
              if (item.hash !== textHash) return item;
              return {
                ...item,
                analysisResults,
                analysisStatus: 'completed',
                status: 'analyzed',
                updatedAt: new Date().toISOString(),
              };
            }));

            // Backend sync is performed by AnalysisStatus endpoint from trusted analysis docs.

            // analysis_text_hash is computed server-side (sha256 of the
            // analyzed transcript) and isn't in this response -- refetch the
            // idea doc so the stale-draft comparison has the real value
            // instead of comparing against whatever it was before this run.
            try {
              const ideaResult = await GetIdeationIdea(idToken, versionId);
              if (ideaResult.status === 200 && ideaResult.myJson) {
                const refreshedHash = ideaResult.myJson.analysis_text_hash || null;
                setVersions((prev) => prev.map((item) => {
                  if (item.id !== versionId || item.hash !== textHash) return item;
                  return { ...item, analysisTextHash: refreshedHash };
                }));
              }
            } catch {
              // Best-effort; the stale banner just stays conservative until the next reload.
            }
          }
        }
      } catch {
        // Keep polling; transient errors are expected.
      }
    }, 2500);
  };

  const handleAnalyze = async () => {
    if (!selectedVersionId) return;

    const target = versions.find((item) => item.id === selectedVersionId);
    if (!target) return;

    const normalized = String(workingText || '').trim();
    if (normalized.length < 10) {
      setError('Please enter at least 10 characters before analysis.');
      return;
    }

    const textHash = computeTextHash(normalized);

    if (cacheMap[textHash]) {
      setVersions((prev) => prev.map((item) => {
        if (item.id !== selectedVersionId) return item;
        return {
          ...item,
          text: normalized,
          hash: textHash,
          analysisResults: cacheMap[textHash],
          analysisStatus: 'completed',
          status: 'analyzed',
          updatedAt: new Date().toISOString(),
        };
      }));
      setError('');
      return;
    }

    try {
      setIsAnalyzing(true);
      setError('');

      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const queued = await AnalyzeTranscript(idToken, normalized, 'anthropic', true, {
        entityType: 'Idea',
        entityId: selectedVersionId,
        entityTextHash: textHash,
      });
      if (queued.status !== 200) {
        throw new Error(queued.myJson?.message || queued.myJson?.detail || 'Failed to queue transcript analysis.');
      }

      const analysisId = extractAnalysisId(queued.myJson);
      if (!analysisId) {
        throw new Error('Analysis ID was not returned from transcript analysis.');
      }

      // The server reuses an existing analysis when the content is byte-identical,
      // returning completed results inline instead of queueing new work.
      const reusedResults = queued.myJson?.status === 'completed' ? queued.myJson?.analysis_results : null;
      if (reusedResults) {
        setVersions((prev) => prev.map((item) => {
          if (item.id !== selectedVersionId) return item;
          return {
            ...item,
            text: normalized,
            hash: textHash,
            analysisId,
            analysisStatus: 'completed',
            analysisResults: reusedResults,
            analysisTextHash: item.analysisTextHash,
            status: 'analyzed',
            updatedAt: new Date().toISOString(),
          };
        }));
        setIsAnalyzing(false);
        const refreshed = await GetIdeationIdea(idToken, selectedVersionId);
        if (refreshed.status === 200 && refreshed.myJson) {
          const refreshedHash = refreshed.myJson.analysis_text_hash || null;
          setVersions((prev) => prev.map((item) => (
            item.id === selectedVersionId ? { ...item, analysisTextHash: refreshedHash } : item
          )));
        }
        return;
      }

      setVersions((prev) => prev.map((item) => {
        if (item.id !== selectedVersionId) return item;
        return {
          ...item,
          text: normalized,
          hash: textHash,
          analysisId,
          analysisStatus: 'queued',
          status: 'draft',
          updatedAt: new Date().toISOString(),
        };
      }));

      await pollAnalysisUntilDone(idToken, analysisId, selectedVersionId, textHash);
    } catch (ex) {
      setIsAnalyzing(false);
      setError(ex.message || 'Failed to analyze text.');
    }
  };

  const renderAnalysisSummary = () => {
    if (!selectedVersion?.analysisResults) {
      return <p className="ideation-muted">No analysis yet for this iteration.</p>;
    }

    if (hasStructuredSections(selectedVersion.analysisResults)) {
      return <StructuredAnalysisView analysis={{ analysis_results: selectedVersion.analysisResults }} />;
    }

    const results = selectedVersion.analysisResults?.analysis || selectedVersion.analysisResults || {};
    const clarity = results.clarity_engagement || null;
    const claims = results.claims_risk || null;
    const cta = results.cta_alignment || null;

    const renderAnalysisBlock = (value) => {
      const { jsonValues, narrative } = extractJsonAndNarrative(value);

      return (
        <>
          {renderNarrativeSection(narrative)}
          {jsonValues.map((jsonValue, idx) => (
            <pre key={`json-${idx}`}>{JSON.stringify(jsonValue, null, 2)}</pre>
          ))}
          {jsonValues.length === 0 && !narrative && (
            <pre>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
          )}
        </>
      );
    };

    return (
      <div className="ideation-summary-grid">
        <div className="ideation-summary-card">
          <h4>Clarity & Engagement</h4>
          {renderAnalysisBlock(clarity)}
        </div>
        <div className="ideation-summary-card">
          <h4>Claims Risk</h4>
          {renderAnalysisBlock(claims)}
        </div>
        <div className="ideation-summary-card">
          <h4>CTA Analysis</h4>
          {renderAnalysisBlock(cta)}
        </div>
      </div>
    );
  };

  return (
    <div className="ideation-container">

      {!hideHeader && (
        <div className="ideation-header">
          <h1>Ideation</h1>
          <p>See your past ideas, refine the current one, and move into editing without leaving the workflow.</p>
        </div>
      )}

      <div className={`ideation-layout ${showIdeaSidebar ? '' : 'ideation-layout-single'}`.trim()}>
        {showIdeaSidebar && (
          <aside className="ideation-versions">
            <div className="ideation-versions-header">
              <h3>Past Ideas</h3>
              <button className="btn-primary" onClick={handleCreateIteration}>+ Start New Idea</button>
            </div>

            <div className="ideation-version-list">
              {isLoadingIdeas && <p className="ideation-muted">Loading ideas...</p>}
              {visibleVersions.length === 0 && (
                <p className="ideation-muted">No saved ideas yet.</p>
              )}

              {visibleVersions.map((item, idx) => {
                const label = `Idea ${visibleVersions.length - idx}`;
                const isLast = idx === visibleVersions.length - 1;
                return (
                  <button
                    key={item.id}
                    ref={isLast ? lastElementRef : undefined}
                    className={`ideation-version-item ${item.id === selectedVersionId ? 'active' : ''}`}
                    onClick={() => handleSelectVersion(item.id)}
                  >
                    <div className="ideation-version-title">{getIdeaLabel(item.text, label)}</div>
                    <div className="ideation-version-preview">{getIdeaPreview(item.text)}</div>
                    <div className="ideation-version-meta">
                      <span>Status: {item.status}</span>
                      <span>{new Date(item.updatedAt).toLocaleString()}</span>
                    </div>
                    <div className="ideation-version-actions">
                      <span className="ideation-open-hint">
                        {item.videoWorkspaceId ? 'Workspace linked' : 'Click to open'}
                      </span>
                      {item.videoWorkspaceId && (
                        <span
                          className="ideation-workspace-link"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenIdeaWorkspace(item);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              handleOpenIdeaWorkspace(item);
                            }
                          }}
                        >
                          Open workspace
                        </span>
                      )}
                      <span
                        className="ideation-delete-link"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteIdea(item.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDeleteIdea(item.id);
                          }
                        }}
                      >
                        Delete
                      </span>
                    </div>
                  </button>
                );
              })}

              {isLoadingMoreIdeas && <p className="ideation-muted">Loading more ideas...</p>}
              {!hasMoreIdeas && visibleVersions.length > 0 && <p className="ideation-muted">You reached the end of ideas.</p>}
            </div>
          </aside>
        )}

        <section className="ideation-editor">
          {/* No title field: the project header above owns the one name, and a
              second one here only ever disagreed with it. */}
          <label htmlFor="idea-text"><strong>Idea Text</strong></label>
          <textarea
            id="idea-text"
            value={workingText}
            onChange={(e) => setWorkingText(e.target.value)}
            placeholder="Type your post idea or transcript draft here..."
            rows={14}
          />
          <div className={`ideation-save-status ${isIdeaDirty ? 'saving' : 'saved'}`} aria-live="polite">
            {isIdeaDirty
              ? 'Saving…'
              : (String(workingText || '').trim() ? 'All changes saved' : 'Autosaves as you type')}
          </div>

          {/* The collaborator docks bottom-right so the draft itself owns the
              page. Its suggestion still lands inline below, where the script
              it would replace is. */}
          <FloatingChatDock
            title="AI Script Collaborator"
            icon={<PenLine size={18} aria-hidden="true" />}
            launcherLabel="Ask AI"
            launcherHint="Chat with the AI script collaborator"
            busy={aiWorking}
            open={isChatOpen}
            onOpen={() => setIsChatOpen(true)}
            onClose={() => setIsChatOpen(false)}
            attention={analysisOffer?.status === 'open'}
            status={
              /* Progress, and a dropped connection — the two things the user
                 can act on. Whether the socket is merely open is plumbing, and
                 now that it connects on first send, "Disconnected" was the
                 normal resting state. */
              aiWorking
                ? <span className="ideation-chat-status connected">Working…</span>
                : (aiReconnecting ? <span className="ideation-chat-status reconnecting">Reconnecting…</span> : null)
            }
          >
          <div className="ideation-chat-panel">
            <div className="ideation-chat-log">
              {aiMessages.length === 0 && (
                <p className="ideation-muted">
                  Ask AI to refine hooks, tone, structure, clarity, or CTA — e.g. “Punch up my first line.”
                  When your draft feels right, run <em>Analyze</em> below for a scored breakdown.
                </p>
              )}
              {aiMessages.map((msg, index) => (
                <div key={`${msg.role}-${index}`} className={`ideation-chat-message ${msg.role}`}>
                  <strong>{msg.role === 'user' ? 'You' : (msg.role === 'system' ? 'Analysis' : 'AI')}:</strong> {msg.text}
                  {msg.usage && (
                    <div className="ideation-chat-usage">
                      {Number(msg.usage.cost_usd || 0) < 0.01 ? 'Cost: under 1¢' : `Cost: $${Number(msg.usage.cost_usd).toFixed(2)}`}
                    </div>
                  )}
                </div>
              ))}

              {analysisOffer?.status === 'open' && (
                <div className="ideation-chat-offer">
                  <p>
                    Your analysis for this draft is ready. Want me to use it to improve the script?
                  </p>
                  <div className="ideation-chat-offer-actions">
                    <button
                      className="btn-primary"
                      onClick={handleAcceptAnalysisOffer}
                      disabled={aiWorking}
                    >
                      Use the analysis
                    </button>
                    <button className="btn-secondary" onClick={handleDismissAnalysisOffer}>
                      Not now
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="ideation-chat-input-row">
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                placeholder="Tell AI what to improve in your script..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendAiMessage();
                  }
                }}
              />
              <button
                className="btn-primary ideation-chat-btn"
                onClick={handleSendAiMessage}
                disabled={aiWorking}
              >
                Send
              </button>
            </div>

          </div>
          </FloatingChatDock>

          {/* The suggested rewrite stays in the page, next to the draft it is
              proposing to replace — reading a diff in a corner panel is worse
              than reading it where the text lives, and it has to be
              actionable whether or not the chat is open. */}
          {aiSuggestedText && (
            <div className="ideation-suggestion-preview">
              <div className="ideation-suggestion-header">
                <h4>Suggestion Preview</h4>
                <span className="ideation-muted">
                  {isSuggestionUnchanged
                    ? 'No changes proposed — identical to your draft'
                    : `${suggestionDiff.changed} changed · ${suggestionDiff.added} added · ${suggestionDiff.removed} removed`}
                </span>
              </div>

              <div className="ideation-suggestion-grid">
                <div className="ideation-suggestion-column">
                  <label>Current Draft</label>
                  <textarea
                    className="ideation-suggestion-textarea"
                    value={workingText}
                    readOnly
                  />
                </div>
                <div className="ideation-suggestion-column">
                  <label>Suggested Draft</label>
                  <textarea
                    className="ideation-suggestion-textarea"
                    value={aiSuggestedText}
                    readOnly
                  />
                </div>
              </div>

              <div className="ideation-chat-actions">
                <button
                  className="btn-primary ideation-apply-btn"
                  onClick={handleApplyAiSuggestion}
                  disabled={aiWorking || isSuggestionUnchanged}
                >
                  Apply AI Draft
                </button>
                <button
                  className="btn-secondary ideation-reject-btn"
                  onClick={handleRejectAiSuggestion}
                >
                  Discard
                </button>
                <span className="ideation-muted">
                  {isSuggestionUnchanged
                    ? 'Nothing to apply — try asking again, or be more specific about what to change'
                    : 'Apply to replace your draft, or discard to keep it as is'}
                </span>
              </div>
            </div>
          )}

          {error && <p className="ideation-error">{error}</p>}

          <div className="ideation-analysis-panel">
            <div className="ideation-analysis-header">
              <div className="ideation-analysis-heading">
                <h3>Analysis</h3>
                <p className="ideation-muted">
                  Happy with your draft? Get a scored breakdown of your hook, tone, and CTA.
                </p>
              </div>
              <button className="btn-primary" onClick={handleAnalyze} disabled={isAnalyzing}>
                {isAnalyzing ? 'Analyzing…' : 'Analyze my script'}
              </button>
            </div>
            {selectedVersion?.analysisStatus && (
              <p className="ideation-muted ideation-analysis-status">Analysis status: {selectedVersion.analysisStatus}</p>
            )}
            {selectedVersion?.analysisStatus === 'completed' && selectedVersion?.analysisId && selectedVersion?.hash !== selectedVersion?.analysisTextHash && (
              <p className="ideation-analysis-stale">
                Analysis reflects an earlier draft — the script has changed since this analysis ran.
              </p>
            )}
            {renderAnalysisSummary()}
          </div>

          {(selectedVersion?.versionHistory?.length || 0) > 0 && (
            <div className="ideation-revisions">
              <h3>Version History</h3>
              <p className="ideation-muted">
                Currently on version {selectedVersion?.version}. The version only advances when you accept
                an AI change — autosave never creates one.
              </p>
              <ul>
                {[...selectedVersion.versionHistory].reverse().map((entry) => (
                  <li key={entry.version} className="ideation-revision-item">
                    <div className="ideation-revision-meta">
                      <span className="ideation-revision-source">
                        v{entry.version} ·{' '}
                        {entry.source === 'original' ? 'Your original draft' : (entry.source === 'revert' ? 'Reverted' : 'AI applied')}
                      </span>
                      <span className="ideation-muted">{new Date(entry.created_at).toLocaleString()}</span>
                    </div>
                    {entry.summary_of_changes && (
                      <p className="ideation-muted">{entry.summary_of_changes}</p>
                    )}
                    <button
                      className="ideation-revert-btn"
                      onClick={() => handleRevertToVersion(entry.version)}
                      disabled={revertingVersion === entry.version || entry.version === selectedVersion?.version}
                    >
                      {revertingVersion === entry.version ? 'Reverting…' : `Revert to v${entry.version}`}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export { Ideation };
